
/** This class represents one attachment that is saved on the server.
This is designed to use the system table "Attachments".  The following fields should be defined at least.

id - Unique ID for the file
content_id - Assigned by the server, globally unique ID.  Assigned upload upload.
content_path - Assigned by the server and contains a full url without server to the download path.  For example /api/attachment/get/$content_id.jpg
content_data - The binary for the file in the Mongo Database
upload_user - Authenticated user (blank if a participant)
upload_participant_id - Authenticated participant (blank if a user)
file_icon - Future use, currently not assigned
mime_type - Assigned by the server based on the type of file.  For example image/jpeg.  Should come from browser during upload.
file_size - Size of the file in bytes

Provided by the caller of this class:
filename - The user's filename if known.  For example, "my_word_doc.docx" or "DCIM10231.jpg"
upload_timezone - The browser or current user's timezone
upload_dt - Assigned during upload by the server
participant_id - Assign this document to a participant
reference_type - optional text
reference_id - Optional text

*/

import { DataServices } from "./DataServices";
import { DataQuery } from "./DataQuery";
import { DataRecord } from "./DataRecord";

export class AttachmentManager
{
	data : Partial<AttachmentManager.AttachmentDataType> = {};

	isSaved()
	{
	}

	constructor()
	{
	}

	static validate(data: AttachmentManager.AttachmentDataType) {
		if (!data.id ||
			!data.content_id ||
			!data.content_path ||
			(!data.upload_user && !data.upload_participant_id) ||
			!data.mime_type ||
			!data.filename ||
			!data.file_size
		) {
			return false;
		}

		return true;
	}

	/** Set the participant that this attachment belongds to */
	setParticipant(pid : string)
	{
		this.data.participant_id = pid;
	}

    /** Set a reference to something, may be different depening on use case and audit type */
    setReference(evReference: string, evReferenceId : string) {
        this.data.reference_type = evReference;
        this.data.reference_id = evReferenceId;
    }

    setFileData(binData : Buffer | string)
    {
        this.data.content_data = Buffer.from(binData);
        this.data.file_size = this.data.content_data.length;
    }

    setFilename(newFilename : string)
    {
        this.data.filename = newFilename;
        // TODO Determine mime type from file extension
    }

    setMimeType(newMime : string)
    {
        this.data.mime_type = newMime;
    }

	/** Save the data and record a data record with the result. */
	async save() : Promise<DataRecord<AttachmentManager.AttachmentDataType>>
	{
		if (!this.data.id)
		{
			let parta = new Date().getTime().toString(36);
			let partb = (Math.ceil(Math.random() * 899999) + 1000000).toString(36);
			this.data.id = "ATT" + parta + partb;
		}

		// Force save the current timezone information
		this.data.upload_dt = new Date().toISOString();
		this.data.upload_timezone = new Date().getTimezoneOffset();

		// Call the API to Save the attachment
		const saveResult = await DataServices.instance().saveAttachment(this.data as AttachmentManager.AttachmentDataType);

		if (saveResult.success) {
			// Need to reload the record from the server
			// because the server has setup several values that we want to return
			const result = await DataQuery.instance().executeQueryById("Attachments", this.data.id);
			return result as DataRecord<AttachmentManager.AttachmentDataType>;
		} else {
			throw new Error(saveResult.message);
		}
	}
}

export namespace AttachmentManager
{
	export interface AttachmentDataType
	{
		id: string;
		participant_id: string;
		filename?: string;
		content_id?: string;
		content_path?: string;
		content_data?: Buffer;
		upload_dt?: string;
		upload_timezone?: number;
		upload_user?: string;
		upload_participant_id?: string;
		file_icon?: string;
		mime_type?: string;
		reference_type?: string;
		reference_id?: string;
		file_size?: number;
	}
}
